import { useTranslation } from "react-i18next";
import styles from "../ExamSessionPage.module.css";
import QuestionContent from "../../../../components/common/QuestionContent/QuestionContent";

const ExamQuestionSection = ({ questions, currentIndex, answers, onGoToQuestion, onOptionChange, onTextChange, isStudent }) => {
  const { t } = useTranslation();
  if (!questions.length || currentIndex < 0 || currentIndex >= questions.length) {
    return null;
  }

  const currentQuestion = questions[currentIndex];

  const isQuestionAnswered = (q) => {
    const a = answers[q.id];
    if (!a) return false;
    if (q.type === "OPEN") {
      return typeof a.textAnswer === "string" && a.textAnswer.trim().length > 0;
    }
    return Array.isArray(a.selectedOptionIds) && a.selectedOptionIds.length > 0;
  };

  const handlePrev = () => {
    if (currentIndex === 0) return;
    onGoToQuestion(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex === questions.length - 1) return;
    onGoToQuestion(currentIndex + 1);
  };

  const renderNavButton = (q, idx) => {
    const answered = isQuestionAnswered(q);
    const isActive = idx === currentIndex;
    const classNames = [styles.questionNavItem, isActive ? styles.questionNavItemActive : "", answered ? styles.questionNavItemAnswered : ""].filter(Boolean).join(" ");
    return (
      <button key={q.id} type="button" className={classNames} onClick={() => onGoToQuestion(idx)}>
        {idx + 1}
      </button>
    );
  };

  // The backend already orders questions grouped by subject (one subject after another). Build the
  // contiguous groups here — keeping each question's global index for navigation — so we can show a
  // subject header per group. Only surfaced when the exam actually spans more than one subject.
  const subjectGroups = [];
  questions.forEach((q, idx) => {
    const name = q.subjectName || null;
    const last = subjectGroups[subjectGroups.length - 1];
    if (last && last.name === name) last.items.push({ q, idx });
    else subjectGroups.push({ name, items: [{ q, idx }] });
  });
  const hasMultipleSubjects = subjectGroups.length > 1;

  return (
    <>
      <div className={styles.questionNav}>
        <div className={styles.questionNavTitle}>{t("pages.examSession.questionsLabel")}</div>
        {hasMultipleSubjects ? (
          <div className={styles.questionNavGroups}>
            {subjectGroups.map((g, gi) => (
              <div key={gi} className={styles.questionNavGroup}>
                <div className={styles.questionNavSubject}>{g.name || t("pages.examSession.otherSubject")}</div>
                <div className={styles.questionNavList}>
                  {g.items.map(({ q, idx }) => renderNavButton(q, idx))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.questionNavList}>
            {questions.map((q, idx) => renderNavButton(q, idx))}
          </div>
        )}
      </div>

      <div className={styles.questionsWrapper}>
        <div className={styles.questionCard}>
          {hasMultipleSubjects && currentQuestion.subjectName && (
            <div className={styles.currentSubjectBanner}>
              <span className={styles.currentSubjectLabel}>{t("pages.examSession.currentSubject")}</span>
              <span className={styles.currentSubjectName}>{currentQuestion.subjectName}</span>
            </div>
          )}
          <div className={styles.questionHeader}>
            <span className={styles.questionIndex}>
              {t("pages.examSession.questionIndex", { current: currentIndex + 1, total: questions.length })}
            </span>
              {!isStudent && <span className={styles.difficultyLevel}>{t("pages.examSession.points", { count: currentQuestion.difficultyLevel })}</span>}
          </div>

          {currentQuestion.attachmentUrl && (
            <div className={styles.attachment}>
              <img src={currentQuestion.attachmentUrl} alt={t("pages.examSession.questionImageAlt")} className={styles.attachmentImage} />
            </div>
          )}

            <div className={styles.questionText}>
                <QuestionContent text={currentQuestion.text} />
            </div>

            {currentQuestion.options && currentQuestion.options.length > 0 && (
                <div className={styles.optionsList}>
                    {currentQuestion.options.map((opt, idx) => {
                        const answer = answers[currentQuestion.id] || {
                            selectedOptionIds: [],
                        };

                        const checked = answer.selectedOptionIds.includes(opt.id);
                        const isMultiple =
                            currentQuestion.type &&
                            currentQuestion.type.toUpperCase() === "MULTIPLE_CHOICE_MULTIPLE";

                        return (
                            <label key={opt.id} className={styles.optionItem}>
                                <input
                                    type={isMultiple ? "checkbox" : "radio"}
                                    name={`q-${currentQuestion.id}`}
                                    checked={checked}
                                    onChange={() => onOptionChange(currentQuestion.id, opt.id, isMultiple)}
                                />

                                <div className={styles.optionLabel}>
                                    <span className={styles.optionPrefix}>
                                      {String.fromCharCode(65 + idx)})
                                    </span>

                                    <div className={styles.optionContent}>
                                        <QuestionContent text={opt.optionText} />
                                        {opt.attachmentUrl && (
                                            <img
                                                src={opt.attachmentUrl}
                                                alt="option"
                                                className={styles.optionAttachmentImage}
                                            />
                                        )}
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </div>
            )}

            {currentQuestion.type === "OPEN" && (
                <div style={{ marginTop: 8 }}>
                    <textarea
                        style={{ width: "100%", minHeight: 160, padding: 12, borderRadius: 4, border: "1px solid #d1d5db", fontFamily: "inherit", fontSize: 15, lineHeight: 1.5, resize: "vertical", boxSizing: "border-box" }}
                        value={(answers[currentQuestion.id] && answers[currentQuestion.id].textAnswer) || ""}
                        onChange={(e) => onTextChange(currentQuestion.id, e.target.value)}
                        placeholder={t("pages.examSession.openAnswerPlaceholder")}
                    />
                </div>
            )}
        </div>

        <div className={styles.navButtons}>
          <button type="button" className={styles.navButton} onClick={handlePrev} disabled={currentIndex === 0}>
            {t("common.previous")}
          </button>
          <button type="button" className={styles.navButton} onClick={handleNext} disabled={currentIndex === questions.length - 1}>
            {t("common.next2")}
          </button>
        </div>
      </div>
    </>
  );
};

export default ExamQuestionSection;
